import { useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { FileSignature, ExternalLink } from 'lucide-react';
import CaRequestDialog from './CaRequestDialog.jsx';
import { formatDateTime, toDate } from '@/lib/date-display.js';

const shortDateTime = (iso) => formatDateTime(iso);

function relativeShort(iso) {
  if (!iso) return '';
  const t = toDate(iso)?.getTime() ?? NaN;
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export default function CaStatusPanel({ item, dealId, onChange }) {
  const status = item?.CA_Status || 'not_sent';
  const signerName = item?.CA_Signer_Name || '';
  const boundAt = item?.CA_Bound_At || '';
  const requestedAt = item?.CA_Requested_At || '';
  const pdfUrl = item?.CA_Bind_PDF_URL || '';

  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = () => { if (dealId) setDialogOpen(true); };

  let pill;
  if (status === 'bound') {
    const label = signerName
      ? `CA bound by ${signerName}${boundAt ? ` on ${shortDateTime(boundAt)}` : ''}`
      : 'CA bound';
    pill = (
      <Badge variant="success" className="gap-1 text-[10px]" title={signerName ? `${signerName} - ${boundAt}` : boundAt}>
        <FileSignature size={11} strokeWidth={2} />
        {label}
      </Badge>
    );
  } else if (status === 'requested' || status === 'sent') {
    pill = (
      <Badge variant="warning" className="gap-1 text-[10px]" title={`Sent ${requestedAt}`}>
        <FileSignature size={11} strokeWidth={2} />
        {requestedAt ? `CA pending, sent ${relativeShort(requestedAt)}` : 'CA pending'}
      </Badge>
    );
  } else {
    pill = (
      <Badge variant="outline" className="gap-1 text-[10px] text-text-muted">
        <FileSignature size={11} strokeWidth={2} />
        CA not sent
      </Badge>
    );
  }

  const primaryBtn = (text) => (
    <Button
      size="sm"
      variant="outline"
      className="w-full justify-start gap-2 border-border-strong hover:border-accent hover:bg-accent-soft hover:text-accent"
      onClick={openDialog}
    >
      <FileSignature size={14} strokeWidth={1.5} />
      <span className="text-xs">{text}</span>
    </Button>
  );

  return (
    <>
      <div className="rounded-card border border-border bg-surface p-3 shadow-sm">
        <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">
          Commercial Aviation Binding
        </h3>
        <div className="mb-2">{pill}</div>
        <div className="flex flex-col gap-1.5">
          {status === 'not_sent' || status === 'not_requested' ? primaryBtn('Send CA binding to insured') : null}
          {status === 'bound' ? primaryBtn('Re-request') : null}
          {status === 'requested' || status === 'sent' ? (
            <Button
              size="sm"
              variant="ghost"
              className="w-full justify-start gap-2 text-text-muted hover:text-text"
              onClick={openDialog}
              title="Mints a NEW signing token"
            >
              <FileSignature size={14} strokeWidth={1.5} />
              <span className="text-xs">Re-request (new link)</span>
            </Button>
          ) : null}
          {pdfUrl && status === 'bound' ? (
            <Button asChild size="sm" variant="ghost" className="w-full justify-start gap-2 text-text-muted hover:text-text">
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={14} strokeWidth={1.5} />
                <span className="text-xs">View signed PDF</span>
              </a>
            </Button>
          ) : null}
        </div>
      </div>
      <CaRequestDialog
        dealId={dealId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onChange={onChange}
      />
    </>
  );
}
